import React from "react";
import { DataGrid } from "@mui/x-data-grid";
import Button from "shared/components/FormElements/Button";
import Grid from "@mui/material/Grid";
import Chip from "@mui/material/Chip";

const STATUS_COLORS = {
  NEW: "default",
  CRAWLED: "default",
  CRAWL_FAILED: "warning",
  SCORED: "primary",
  APPROVED: "info",
  OUTREACH: "warning",
  CONTACTED: "warning",
  REPLIED: "success",
  SOLD: "success",
  REJECTED: "error",
  AUTO_REJECTED: "default",
  BOUNCED: "error",
};

const SCORE_COLOR = (score) => {
  if (score >= 70) return "#2e7d32";
  if (score >= 40) return "#ed6c02";
  return "#d32f2f";
};

const STATUS_OPTIONS = [
  "",
  "NEW",
  "CRAWLED",
  "CRAWL_FAILED",
  "SCORED",
  "APPROVED",
  "OUTREACH",
  "CONTACTED",
  "REPLIED",
  "SOLD",
  "REJECTED",
  "AUTO_REJECTED",
  "BOUNCED",
];
const CATEGORY_OPTIONS = [
  "",
  "YELLOW_BUS_CONTRACTOR",
  "SPED_TRANSPORT",
  "CHARTER_PLUS_SCHOOL",
  "TOO_BIG",
  "NOT_SCHOOL",
  "OTHER",
];
const SOURCE_OPTIONS = ["", "fmcsa", "places", "search", "manual", "directory", "seed"];
const ICP_OPTIONS = ["", "A", "B", "C", "exclude"];
const STATE_OPTIONS = [
  "", "AL", "AK", "AZ", "AR", "CA", "CO", "CT", "DE", "DC", "FL", "GA", "HI", "ID", "IL", "IN", "IA",
  "KS", "KY", "LA", "ME", "MD", "MA", "MI", "MN", "MS", "MO", "MT", "NE", "NV", "NH", "NJ", "NM",
  "NY", "NC", "ND", "OH", "OK", "OR", "PA", "RI", "SC", "SD", "TN", "TX", "UT", "VT", "VA", "WA",
  "WV", "WI", "WY",
];

const columns = [
  {
    field: "companyName",
    headerName: "Company",
    flex: 1.4,
  },
  {
    field: "state",
    headerName: "ST",
    width: 70,
    valueGetter: (params) => params.row.state || params.row.region || "",
  },
  {
    field: "driversReported",
    headerName: "Drivers",
    width: 90,
    valueGetter: (params) => params.row.driversReported ?? "-",
  },
  {
    field: "website",
    headerName: "Domain",
    flex: 1,
    renderCell: (params) => {
      const value = params.value || "";
      if (!value || /^(usdot|places|manual):/i.test(value)) {
        return <span style={{ color: "#ccc" }}>{value || "none"}</span>;
      }
      return (
        <a href={`https://${value}`} target="_blank" rel="noopener noreferrer" style={{ color: "#1976d2" }}>
          {value}
        </a>
      );
    },
  },
  {
    field: "score",
    headerName: "Score",
    width: 80,
    valueGetter: (params) => params.row.scoring?.score ?? "-",
    renderCell: (params) => {
      const score = params.row.scoring?.score;
      if (score === null || score === undefined) return "-";
      return <span style={{ fontWeight: "bold", color: SCORE_COLOR(score) }}>{score}</span>;
    },
  },
  {
    field: "icpTier",
    headerName: "ICP",
    width: 80,
    renderCell: (params) => {
      const tier = params.row.icpTier;
      if (!tier) return "-";
      const color = tier === "A" ? "success" : tier === "B" ? "warning" : "default";
      return <Chip label={tier} size="small" color={color} variant="outlined" />;
    },
  },
  {
    field: "category",
    headerName: "Category",
    flex: 1,
    valueGetter: (params) => params.row.scoring?.category || "-",
    renderCell: (params) => {
      const cat = params.row.scoring?.category;
      if (!cat || cat === "OTHER") return <span style={{ color: "#999" }}>{cat || "-"}</span>;
      return <Chip label={cat.replace(/_/g, " ")} size="small" variant="outlined" />;
    },
  },
  {
    field: "status",
    headerName: "Status",
    flex: 1,
    renderCell: (params) => <Chip label={params.value} size="small" color={STATUS_COLORS[params.value] || "default"} />,
  },
  {
    field: "email",
    headerName: "Email / Phone",
    flex: 1,
    valueGetter: (params) => {
      const emails = params.row.crawlData?.emails || [];
      return emails[0] || params.row.phone || "";
    },
    renderCell: (params) => {
      const emails = params.row.crawlData?.emails || [];
      const contact = emails[0] || params.row.phone;
      if (!contact) return <span style={{ color: "#ccc" }}>none</span>;
      return <span style={{ fontSize: "12px" }}>{contact}</span>;
    },
  },
  {
    field: "source",
    headerName: "Source",
    width: 90,
  },
];

const LeadList = ({ leads, total, loading, filters, onFiltersChange, onSelectLead, onNewLead, onRunDiscovery }) => {
  const handleChange = (key) => (e) => {
    onFiltersChange({ ...filters, [key]: e.target.value, page: 1 });
  };

  const selectStyle = { width: "100%", padding: "8px", borderRadius: "4px", border: "1px solid #ccc" };

  return (
    <div>
      <Grid container spacing={2} alignItems="center" style={{ marginBottom: "12px" }}>
        <Grid item xs={12} sm={2}>
          <select value={filters.status} onChange={handleChange("status")} style={selectStyle}>
            <option value="">All Statuses</option>
            {STATUS_OPTIONS.filter(Boolean).map((s) => (
              <option key={s} value={s}>{s}</option>
            ))}
          </select>
        </Grid>
        <Grid item xs={6} sm={1.5}>
          <select value={filters.state || ""} onChange={handleChange("state")} style={selectStyle}>
            <option value="">All States</option>
            {STATE_OPTIONS.filter(Boolean).map((s) => (
              <option key={s} value={s}>{s}</option>
            ))}
          </select>
        </Grid>
        <Grid item xs={6} sm={1.2}>
          <select value={filters.icpTier || ""} onChange={handleChange("icpTier")} style={selectStyle}>
            <option value="">ICP</option>
            {ICP_OPTIONS.filter(Boolean).map((s) => (
              <option key={s} value={s}>{s}</option>
            ))}
          </select>
        </Grid>
        <Grid item xs={12} sm={2}>
          <select value={filters.category || ""} onChange={handleChange("category")} style={selectStyle}>
            <option value="">All Categories</option>
            {CATEGORY_OPTIONS.filter(Boolean).map((c) => (
              <option key={c} value={c}>{c.replace(/_/g, " ")}</option>
            ))}
          </select>
        </Grid>
        <Grid item xs={6} sm={1.3}>
          <select value={filters.source || ""} onChange={handleChange("source")} style={selectStyle}>
            <option value="">Source</option>
            {SOURCE_OPTIONS.filter(Boolean).map((s) => (
              <option key={s} value={s}>{s}</option>
            ))}
          </select>
        </Grid>
        <Grid item xs={12} sm={2}>
          <input
            type="text"
            placeholder="Search company, USDOT, phone..."
            value={filters.search}
            onChange={handleChange("search")}
            style={selectStyle}
          />
        </Grid>
        <Grid item xs={6} sm={1}>
          <Button onClick={onNewLead} size="small">+ New</Button>
        </Grid>
        <Grid item xs={6} sm={1}>
          <Button onClick={onRunDiscovery} size="small" inverse>Discover</Button>
        </Grid>
      </Grid>

      <div style={{ width: "100%", flex: 1 }}>
        <DataGrid
          rows={leads}
          columns={columns}
          getRowId={(row) => row._id}
          loading={loading}
          pageSize={50}
          rowsPerPageOptions={[25, 50, 100]}
          onRowClick={(params) => onSelectLead(params.row)}
          disableSelectionOnClick
          autoHeight
          density="compact"
          sx={{
            "& .MuiDataGrid-row": { cursor: "pointer" },
            "& .MuiDataGrid-row:hover": { backgroundColor: "#f0f7ff" },
          }}
        />
      </div>
      <p style={{ color: "#666", fontSize: "12px", marginTop: "4px" }}>
        Showing {leads.length} of {total} US leads
      </p>
    </div>
  );
};

export default LeadList;
